import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, KeyRound, ShieldCheck, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PhoenixMark } from "@/components/PhoenixLogo";

const inputCls = "w-full px-3 py-2.5 bg-white border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-phoenix-navy/30";

export default function OnboardInvitePage() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { acceptInvite } = useAuth();
    const [invite, setInvite] = useState(null);
    const [loadError, setLoadError] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get(`/onboard/invite/${token}`)
            .then((r) => setInvite(r.data))
            .catch((e) => setLoadError(e?.response?.data?.detail || "This invitation link is invalid or has expired."));
    }, [token]);

    const submit = async () => {
        setError("");
        if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
        if (password !== confirm) { setError("Passwords do not match."); return; }
        setSaving(true);
        try {
            await acceptInvite(token, password);
            navigate("/provider", { replace: true });
        } catch (e) {
            setError(e?.response?.data?.detail || "Could not complete sign-up. Try again.");
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-phoenix-navy flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                <div className="flex justify-center mb-6"><PhoenixMark size={52} /></div>
                <div data-testid="onboard-invite-card" className="bg-white rounded-2xl p-6 sm:p-8 space-y-5">
                    {loadError ? (
                        <div data-testid="invite-load-error" className="text-center space-y-3">
                            <AlertTriangle className="w-10 h-10 text-destructive mx-auto" />
                            <h1 className="font-display font-bold text-xl text-phoenix-navy">Invitation unavailable</h1>
                            <p className="text-sm text-muted-foreground">{loadError}</p>
                            <button onClick={() => navigate("/login")} className="text-sm font-semibold text-phoenix-navy underline">
                                Go to sign in
                            </button>
                        </div>
                    ) : !invite ? (
                        <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-phoenix-navy" /></div>
                    ) : invite.already_accepted ? (
                        <div className="text-center space-y-3">
                            <ShieldCheck className="w-10 h-10 text-success mx-auto" />
                            <h1 className="font-display font-bold text-xl text-phoenix-navy">Already set up</h1>
                            <p className="text-sm text-muted-foreground">This invitation has already been used. Please sign in to continue your onboarding.</p>
                            <button data-testid="invite-goto-login" onClick={() => navigate("/login")}
                                className="px-4 py-2.5 bg-phoenix-navy text-white text-sm font-semibold rounded-lg">Sign in</button>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center gap-3">
                                <span className="w-10 h-10 rounded-xl bg-phoenix-navy text-white flex items-center justify-center"><KeyRound className="w-5 h-5" /></span>
                                <div>
                                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-phoenix-orange">Welcome to Blue Phoenix</div>
                                    <h1 className="font-display font-bold text-xl text-phoenix-navy">Set your password</h1>
                                </div>
                            </div>
                            <p className="text-sm text-muted-foreground">
                                Hi <span className="font-semibold text-foreground">{invite.name}</span> — you've been invited to join the
                                care team at <span className="font-semibold text-foreground">{invite.facility_name}</span>.
                                Create a password to begin onboarding ({invite.email}).
                            </p>
                            {error && <p data-testid="invite-error" className="text-sm text-destructive">{error}</p>}
                            <input data-testid="invite-password" type="password" className={inputCls} placeholder="Create a password (min 8 chars)"
                                value={password} onChange={(e) => setPassword(e.target.value)} />
                            <input data-testid="invite-password-confirm" type="password" className={inputCls} placeholder="Confirm password"
                                value={confirm} onChange={(e) => setConfirm(e.target.value)} />
                            <button data-testid="invite-submit" disabled={saving || password.length < 8 || password !== confirm} onClick={submit}
                                className="w-full px-4 py-3 bg-phoenix-navy text-white text-sm font-semibold rounded-xl hover:bg-phoenix-navy-2 disabled:opacity-50 transition-all">
                                {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Create account & start onboarding"}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
